// priceTab/container.js - Updated with dynamic pricing toggle state
import React, { useState, useEffect } from 'react';
import { getTariffs, updateTariff, createTariffRule } from 'api';
import PricingTabView from './view';

const daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const PricingTabContainer = ({
  workArea,
  floor,
  venueInfo,
  prices,
  VAT_RATE,
  isUpdating,
  handlePriceChangeWithoutVAT,
  handlePriceChangeWithVAT,
  handleSavePricing,
  showSnackbar // Add this prop to use parent's snackbar
}) => {
  // State for tariff data
  const [tariffs, setTariffs] = useState([]);
  const [showNewRuleForm, setShowNewRuleForm] = useState({});
  const [isVatInclusive, setIsVatInclusive] = useState(false);
  const [isLoadingTariffs, setIsLoadingTariffs] = useState(false);
  const [tariffError, setTariffError] = useState('');
  const [isSavingRule, setIsSavingRule] = useState(false);
  const [newRules, setNewRules] = useState({});
  const [tariffSavingState, setTariffSavingState] = useState({});
  const [editingPrices, setEditingPrices] = useState({});
  
  // New state for dynamic pricing toggle
  const [dynamicPricingEnabled, setDynamicPricingEnabled] = useState(false);

  // Helper function to safely get days_of_week array
  const safeDaysOfWeek = (daysArray) => {
    return Array.isArray(daysArray) ? daysArray : [];
  };

  // Helper function to safely check if a day is included
  const isDayIncluded = (daysArray, day) => {
    const safeDays = safeDaysOfWeek(daysArray);
    return safeDays.includes(day);
  };

  // Initialize a new rule template
  const getNewRuleTemplate = () => ({
    days_of_week: [],
    occupancy_min: 0,
    occupancy_max: 1,
    unit_price: 0,
    status: "AC",
    occupancy_in_days: 1,
    disabled_if_not_week: false,
    disabled_if_not_month: false,
    occupancy_instant_booking: "0.00",
    space_limit: 0,
    days_of_month: []
  });

  // Fetch tariff data when component mounts or workArea changes
  useEffect(() => {
    if (workArea?.slug) {
      fetchTariffData();
    }
  }, [workArea?.slug]);

  // Reset editing prices when VAT inclusion changes
  useEffect(() => {
    setEditingPrices({});
  }, [isVatInclusive]);

  // Set dynamic pricing enabled based on existing rules
  useEffect(() => {
    // We don't need to automatically set dynamicPricingEnabled anymore
    // since the toggle state is now determined by the rules' status directly
  }, [tariffs]);

  // Fetch tariff data from API
  const fetchTariffData = async () => {
    setIsLoadingTariffs(true);
    setTariffError('');

    try {
      const response = await getTariffs({
        work_area: workArea.slug,
        booking_type: 'HD'
      });

      if (response?.tariffs && response.tariffs.length > 0) {
        const hdTariffs = response.tariffs.map(tariff => ({
          ...tariff,
          rules: Array.isArray(tariff.rules) ? tariff.rules.map(rule => ({
            ...rule,
            days_of_week: safeDaysOfWeek(rule.days_of_week),
            days_of_month: Array.isArray(rule.days_of_month) ? rule.days_of_month : []
          })) : []
        }));
        
        setTariffs(hdTariffs);

        const newRuleState = {};
        const showFormState = {};

        hdTariffs.forEach(tariff => {
          newRuleState[tariff.slug] = getNewRuleTemplate();
          showFormState[tariff.slug] = false;
        });

        setNewRules(newRuleState);
        setShowNewRuleForm(showFormState);
      } else {
        setTariffs([]);
      }
    } catch (error) {
      console.error('Error fetching tariff data:', error);
      setTariffError('Failed to load tariff data. Please try again.');
    } finally {
      setIsLoadingTariffs(false);
    }
  };

  // Function to calculate display price based on VAT inclusion
  const getDisplayPrice = (unitPrice) => {
    if (unitPrice === '' || unitPrice === null || unitPrice === undefined) {
      return '';
    }

    const editKey = `price-${unitPrice}`;
    if (editingPrices[editKey] !== undefined) {
      return editingPrices[editKey];
    }

    let price = parseFloat(unitPrice);
    
    if (isNaN(price)) {
      return '0.00';
    }

    if (isVatInclusive) {
      price *= (1 + (VAT_RATE / 100));
    }

    return price.toFixed(2);
  };

  // Convert display price back to VAT exclusive
  const getVatExclusivePrice = (displayPrice) => {
    if (displayPrice === '' || displayPrice === null || displayPrice === undefined) {
      return '0.00';
    }

    let price = parseFloat(displayPrice);
    
    if (isNaN(price)) {
      return '0.00';
    }

    if (isVatInclusive) {
      price = price / (1 + (VAT_RATE / 100));
    }

    return price.toFixed(2);
  };

  // Helper to generate a unique key for a rule's price
  const getPriceKey = (tariffSlug, ruleIndex) => {
    return `${tariffSlug}-${ruleIndex}`;
  };

  // Handle day toggle for existing rules
  const handleDayToggle = (tariffSlug, ruleIndex, day) => {
    const updatedTariffs = [...tariffs];
    const tariffIndex = updatedTariffs.findIndex(t => t.slug === tariffSlug);

    if (tariffIndex === -1) return;

    const tariffToUpdate = updatedTariffs[tariffIndex];

    if (!tariffToUpdate.rules) {
      tariffToUpdate.rules = [];
    }

    const rules = [...tariffToUpdate.rules];

    if (rules[ruleIndex]) {
      const currentDays = safeDaysOfWeek(rules[ruleIndex].days_of_week);
      const days = isDayIncluded(currentDays, day)
        ? currentDays.filter(d => d !== day)
        : [...currentDays, day];

      rules[ruleIndex] = { ...rules[ruleIndex], days_of_week: days };
      tariffToUpdate.rules = rules;

      updatedTariffs[tariffIndex] = tariffToUpdate;
      setTariffs(updatedTariffs);
    }
  };

  // Handle slider change for existing rules
  const handleSliderChange = (tariffSlug, ruleIndex, newValue) => {
    const updatedTariffs = [...tariffs];
    const tariffIndex = updatedTariffs.findIndex(t => t.slug === tariffSlug);

    if (tariffIndex === -1) return;

    const tariffToUpdate = updatedTariffs[tariffIndex];

    if (!tariffToUpdate.rules) {
      tariffToUpdate.rules = [];
    }

    const rules = [...tariffToUpdate.rules];

    if (rules[ruleIndex]) {
      rules[ruleIndex] = {
        ...rules[ruleIndex],
        occupancy_min: (newValue[0] / 100).toFixed(2),
        occupancy_max: (newValue[1] / 100).toFixed(2)
      };

      tariffToUpdate.rules = rules;
      updatedTariffs[tariffIndex] = tariffToUpdate;
      setTariffs(updatedTariffs);
    }
  };

  // Handle input change for existing rules
  const handleInputChange = (tariffSlug, ruleIndex, value) => {
    const priceKey = getPriceKey(tariffSlug, ruleIndex);
    setEditingPrices(prev => ({
      ...prev,
      [priceKey]: value
    }));
  };

  // Handle blur event for price inputs
  const handlePriceBlur = (tariffSlug, ruleIndex) => {
    const priceKey = getPriceKey(tariffSlug, ruleIndex);
    const currentValue = editingPrices[priceKey];
    
    let finalValue = '0.00';
    if (currentValue !== undefined && currentValue !== '') {
      const parsedValue = parseFloat(currentValue);
      if (!isNaN(parsedValue)) {
        finalValue = parsedValue.toFixed(2);
      }
    }
    
    const updatedTariffs = [...tariffs];
    const tariffIndex = updatedTariffs.findIndex(t => t.slug === tariffSlug);
    
    if (tariffIndex !== -1) {
      const tariffToUpdate = updatedTariffs[tariffIndex];
      if (tariffToUpdate.rules && tariffToUpdate.rules[ruleIndex]) {
        const vatExclusivePrice = isVatInclusive 
          ? getVatExclusivePrice(finalValue)
          : finalValue;
        
        tariffToUpdate.rules[ruleIndex].unit_price = vatExclusivePrice;
        updatedTariffs[tariffIndex] = tariffToUpdate;
        setTariffs(updatedTariffs);
      }
    }
    
    setEditingPrices(prev => {
      const newState = {...prev};
      delete newState[priceKey];
      return newState;
    });
  };

  // Handle tariff base price change
  const handleTariffPriceChange = (tariffSlug, value) => {
    const priceKey = `tariff-${tariffSlug}`;
    setEditingPrices(prev => ({
      ...prev,
      [priceKey]: value
    }));
  };

  // Handle blur for tariff base price
  const handleTariffPriceBlur = (tariffSlug) => {
    const priceKey = `tariff-${tariffSlug}`;
    const currentValue = editingPrices[priceKey];
    
    let finalValue = '0.00';
    if (currentValue !== undefined && currentValue !== '') {
      const parsedValue = parseFloat(currentValue);
      if (!isNaN(parsedValue)) {
        finalValue = parsedValue.toFixed(2);
      }
    }
    
    const updatedTariffs = [...tariffs];
    const tariffIndex = updatedTariffs.findIndex(t => t.slug === tariffSlug);
    
    if (tariffIndex !== -1) {
      const vatExclusivePrice = isVatInclusive 
        ? getVatExclusivePrice(finalValue)
        : finalValue;
        
      updatedTariffs[tariffIndex].unit_price = vatExclusivePrice;
      setTariffs(updatedTariffs);
    }
    
    setEditingPrices(prev => {
      const newState = {...prev};
      delete newState[priceKey];
      return newState;
    });
  };

  // Handle changes for new rule
  const handleNewRuleChange = (tariffSlug, field, value) => {
    if (field === 'unit_price') {
      const priceKey = `new-${tariffSlug}`;
      setEditingPrices(prev => ({
        ...prev,
        [priceKey]: value
      }));
    } else {
      setNewRules(prev => ({
        ...prev,
        [tariffSlug]: {
          ...prev[tariffSlug],
          [field]: value
        }
      }));
    }
  };

  // Handle blur for new rule price
  const handleNewRulePriceBlur = (tariffSlug) => {
    const priceKey = `new-${tariffSlug}`;
    const currentValue = editingPrices[priceKey];
    
    let finalValue = '0.00';
    if (currentValue !== undefined && currentValue !== '') {
      const parsedValue = parseFloat(currentValue);
      if (!isNaN(parsedValue)) {
        finalValue = parsedValue.toFixed(2);
      }
    }
    
    const vatExclusivePrice = isVatInclusive 
      ? getVatExclusivePrice(finalValue)
      : finalValue;
      
    setNewRules(prev => ({
      ...prev,
      [tariffSlug]: {
        ...prev[tariffSlug],
        unit_price: vatExclusivePrice
      }
    }));
    
    setEditingPrices(prev => {
      const newState = {...prev};
      delete newState[priceKey];
      return newState;
    });
  };

  // Handle day toggle for new rule
  const handleNewRuleDayToggle = (tariffSlug, day) => {
    const currentRule = newRules[tariffSlug] || getNewRuleTemplate();
    const currentDays = safeDaysOfWeek(currentRule.days_of_week);
    const days = isDayIncluded(currentDays, day)
      ? currentDays.filter(d => d !== day)
      : [...currentDays, day];

    setNewRules(prev => ({
      ...prev,
      [tariffSlug]: {
        ...prev[tariffSlug],
        days_of_week: days
      }
    }));
  };

  // Save tariff changes - Updated to use showSnackbar
  const handleSaveTariff = async (tariffSlug) => {
    const tariffToSave = tariffs.find(t => t.slug === tariffSlug);
    if (!tariffToSave) return;

    try {
      setTariffSavingState(prev => ({ ...prev, [tariffSlug]: true }));

      const tariffData = {
        slug: tariffToSave.slug,
        tariff: {
          name: tariffToSave.name,
          status: tariffToSave.status,
          start: tariffToSave.start,
          end: tariffToSave.end,
          unit_price: tariffToSave.unit_price,
          booking_type: "HD",
          venue: tariffToSave.venue,
          client: tariffToSave.client,
          group: tariffToSave.group
        },
        rules: (tariffToSave.rules || []).map(rule => ({
          ...rule,
          tariff: tariffToSave.id,
          days_of_week: safeDaysOfWeek(rule.days_of_week),
          days_of_month: Array.isArray(rule.days_of_month) ? rule.days_of_month : []
        }))
      };

      const updatedTariffData = await updateTariff(tariffData);

      setTariffs(prevTariffs => {
        return prevTariffs.map(tariff => {
          if (tariff.slug === tariffSlug) {
            return {
              ...tariff,
              ...updatedTariffData.tariff,
              rules: (updatedTariffData.tariff_rules || []).map(rule => ({
                ...rule,
                days_of_week: safeDaysOfWeek(rule.days_of_week),
                days_of_month: Array.isArray(rule.days_of_month) ? rule.days_of_month : []
              }))
            };
          }
          return tariff;
        });
      });

      // Use green success toast instead of setting tariffError
      if (showSnackbar) {
        showSnackbar(`Tariff "${tariffToSave.name}" saved successfully!`, 'success');
      }

    } catch (error) {
      console.error('Error saving tariff data:', error);
      if (showSnackbar) {
        showSnackbar(`Failed to save tariff "${tariffToSave.name}". Please try again.`, 'error');
      }
    } finally {
      setTariffSavingState(prev => ({ ...prev, [tariffSlug]: false }));
    }
  };

  // Add new tariff rule - Updated to use showSnackbar
  const addTariffRule = async (tariffSlug) => {
    const tariff = tariffs.find(t => t.slug === tariffSlug);
    if (!tariff) {
      if (showSnackbar) {
        showSnackbar('Cannot add rule: Tariff not found', 'error');
      }
      return;
    }

    if (tariff.rules && tariff.rules.length >= 3) {
      if (showSnackbar) {
        showSnackbar('Cannot add more than 3 rules per tariff', 'warning');
      }
      return;
    }

    try {
      setIsSavingRule(true);

      const ruleToCreate = {
        ...newRules[tariffSlug],
        tariff: tariffSlug,
        days_of_week: safeDaysOfWeek(newRules[tariffSlug]?.days_of_week),
        days_of_month: Array.isArray(newRules[tariffSlug]?.days_of_month) ? newRules[tariffSlug].days_of_month : []
      };

      const createdRule = await createTariffRule(ruleToCreate);

      if (createdRule) {
        setTariffs(prevTariffs => {
          return prevTariffs.map(t => {
            if (t.slug === tariffSlug) {
              return {
                ...t,
                rules: [...(t.rules || []), {
                  ...createdRule,
                  days_of_week: safeDaysOfWeek(createdRule.days_of_week),
                  days_of_month: Array.isArray(createdRule.days_of_month) ? createdRule.days_of_month : []
                }]
              };
            }
            return t;
          });
        });
      }

      setNewRules(prev => ({
        ...prev,
        [tariffSlug]: getNewRuleTemplate()
      }));

      setShowNewRuleForm(prev => ({
        ...prev,
        [tariffSlug]: false
      }));

      // Use green success toast
      if (showSnackbar) {
        showSnackbar(`New rule added successfully to "${tariff.name}"`, 'success');
      }
    } catch (error) {
      console.error('Error creating new rule:', error);
      if (showSnackbar) {
        showSnackbar(`Failed to create new rule for "${tariff.name}". Please try again.`, 'error');
      }
    } finally {
      setIsSavingRule(false);
    }
  };

  // Handle tariff status toggle
  const handleTariffStatusToggle = (tariffSlug, newStatus) => {
    const updatedTariffs = [...tariffs];
    const tariffIndex = updatedTariffs.findIndex(t => t.slug === tariffSlug);
    if (tariffIndex !== -1) {
      updatedTariffs[tariffIndex].status = newStatus || (updatedTariffs[tariffIndex].status === 'AC' ? 'IN' : 'AC');
      setTariffs(updatedTariffs);
    }
  };

  // Handle dynamic pricing toggle - this controls ALL rules status
  const handleDynamicPricingToggle = (tariffSlug, enabled) => {
    const updatedTariffs = [...tariffs];
    const tariffIndex = updatedTariffs.findIndex(t => t.slug === tariffSlug);

    if (tariffIndex !== -1) {
      const tariffToUpdate = updatedTariffs[tariffIndex];

      if (tariffToUpdate.rules && tariffToUpdate.rules.length > 0) {
        const rules = tariffToUpdate.rules.map(rule => ({
          ...rule,
          status: enabled ? 'AC' : 'IN'
        }));

        tariffToUpdate.rules = rules;
        updatedTariffs[tariffIndex] = tariffToUpdate;
        setTariffs(updatedTariffs);
      }
    }
  };

  // Handle tariff date changes
  const handleTariffDateChange = (tariffSlug, field, value) => {
    const updatedTariffs = [...tariffs];
    const tariffIndex = updatedTariffs.findIndex(t => t.slug === tariffSlug);
    if (tariffIndex !== -1) {
      const dateValue = field === 'start' ? `${value}T00:00:00` : `${value}T23:59:59`;
      updatedTariffs[tariffIndex][field] = dateValue;
      setTariffs(updatedTariffs);
    }
  };

  // Toggle new rule form visibility
  const toggleNewRuleForm = (tariffSlug) => {
    setShowNewRuleForm(prev => ({
      ...prev,
      [tariffSlug]: !prev[tariffSlug]
    }));
  };

  const handlers = {
    handleDayToggle,
    handleSliderChange,
    handleInputChange,
    handlePriceBlur,
    handleTariffPriceChange,
    handleTariffPriceBlur,
    handleNewRuleChange,
    handleNewRulePriceBlur,
    handleNewRuleDayToggle,
    handleSaveTariff,
    addTariffRule,
    handleTariffStatusToggle,
    handleTariffDateChange,
    toggleNewRuleForm,
    setIsVatInclusive,
    setTariffError,
    setDynamicPricingEnabled,
    handleDynamicPricingToggle
  };

  const state = {
    tariffs,
    showNewRuleForm,
    isVatInclusive,
    isLoadingTariffs,
    tariffError,
    isSavingRule,
    newRules,
    tariffSavingState,
    editingPrices,
    dynamicPricingEnabled
  };

  const utils = {
    getDisplayPrice,
    getVatExclusivePrice,
    getPriceKey,
    safeDaysOfWeek,
    isDayIncluded,
    daysOfWeek
  };

  return (
    <PricingTabView
      {...state}
      {...handlers}
      {...utils}
      VAT_RATE={VAT_RATE}
      isUpdating={isUpdating}
    />
  );
};

export default PricingTabContainer;